'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { MenuItem } from '@/widgets/Navbar/MobileMenu/_components/MenuItem';
import { MenuItems, MobileMenuProps } from '@/widgets/Navbar/MobileMenu/models';

import { getCartItemsQuantity } from '@/utilities/cart';

import { useCartStore } from '@/stores/cart/useCartStore';

import { mobileMenuItemsList } from '@/constants/menuItems';

export const MobileMenu = ({ isDark }: MobileMenuProps) => {
    const pathname = usePathname();
    const cartItemsCount = useCartStore((state) => getCartItemsQuantity(state.cart?.items));

    const normalizePath = (path?: string) => (path ? path.replace(/\/+$/, '') || '/' : '');

    const handleActiveLink = (menu: MenuItems) => {
        const normalizedPathname = normalizePath(pathname);

        if (menu.matchPaths?.length) {
            return menu.matchPaths.some((path) => {
                const normalizedPath = normalizePath(path);

                if (normalizedPath === '/') {
                    return normalizedPathname === normalizedPath;
                }

                return normalizedPathname === normalizedPath || normalizedPathname.startsWith(`${normalizedPath}/`);
            });
        }

        const normalizedMenuLink = normalizePath(menu.link);

        if (!normalizedMenuLink) {
            return false;
        }

        if (normalizedMenuLink === '/') {
            return normalizedPathname === normalizedMenuLink;
        }

        return normalizedPathname === normalizedMenuLink || normalizedPathname.startsWith(`${normalizedMenuLink}/`);
    };

    return (
        <div className='pointer-events-none fixed bottom-0 right-0 z-40 w-full px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] md:hidden'>
            <nav
                className={`pointer-events-auto mx-auto max-w-[380px] overflow-hidden rounded-[28px] border border-solid px-1.5 py-1.5
                    shadow-[0_10px_30px_rgba(15,23,42,0.12)] backdrop-blur-2xl backdrop-saturate-150 ${
                        isDark ? 'border-white/10 bg-neutral-950/55 shadow-[0_10px_30px_rgba(0,0,0,0.45)]' : 'border-white/60 bg-white/55'
                    }`}
                aria-label='منوی اصلی'>
                <ul className='my-0 grid w-full grid-cols-4 gap-0.5 p-0'>
                    {mobileMenuItemsList.map((list) => {
                        if (!list.link) {
                            return null;
                        }

                        const isActive = handleActiveLink(list);
                        const tabClassName = `relative flex min-h-11 w-full cursor-pointer flex-col items-center justify-center rounded-full
                            px-1 py-1 transition-[transform,background-color] duration-200 ease-out active:scale-[0.97]
                            motion-reduce:transition-none motion-reduce:active:scale-100 ${
                                isActive ? (isDark ? 'bg-white/10' : 'bg-white/70') : 'bg-transparent'
                            }`;

                        return (
                            <li key={list.id} className='list-none'>
                                <Link
                                    href={list.link}
                                    prefetch={list.prefetch}
                                    className={tabClassName}
                                    aria-current={isActive ? 'page' : undefined}
                                    aria-label={list.title}>
                                    <MenuItem
                                        active={isActive}
                                        badgeContent={list.title === 'سبد خرید' && cartItemsCount ? cartItemsCount : null}
                                        isDark={isDark}
                                        item={list}
                                    />
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </div>
    );
};
